import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Save, Pencil, ImageIcon, RotateCcw } from 'lucide-react'
import { useUpdateManagedDevice, useUpdateManagedDeviceIcon } from '../../../../hooks/useManagedDevices'
import { vpnNetworksApi } from '../../../../services/vpnNetworksApi'
import DeviceKindIcon from '../../../../components/DeviceKindIcon'
import IconPickerModal from '../../../../components/Groups/IconPickerModal'
import ChangeVirtualNetworkConfirmModal from '../../../../components/Modal/ChangeVirtualNetworkConfirmModal'

const FIELDS = [
  { key: 'name', label: 'Nome' },
  { key: 'description', label: 'Descrição' },
  { key: 'sshPort', label: 'Porta SSH', type: 'number', section: 'host' },
  { key: 'remoteDisplayPort', label: 'Porta Remote Display', type: 'number', section: 'host' },
  { key: 'apiUrl', label: 'API URL', section: 'router' },
  { key: 'apiUsername', label: 'API Username', section: 'router' },
  { key: 'firmwareVersion', label: 'Firmware', readOnly: true },
  { key: 'model', label: 'Modelo', readOnly: true },
  { key: 'serialNumber', label: 'Serial', readOnly: true },
]

export default function EditDeviceSection({ device, deviceType }) {
  const { t } = useTranslation()
  const isRouter = deviceType === 'router'
  // Mobile (MDM): sem campos de host (SSH/Remote Display) — o agente cuida disso.
  const isMobile = deviceType === 'mobile'
  const update = useUpdateManagedDevice()
  const updateIcon = useUpdateManagedDeviceIcon()
  const [form, setForm] = useState(() => snapshot(device))
  const [savedAt, setSavedAt] = useState(null)
  const [error, setError] = useState(null)
  const [iconPickerOpen, setIconPickerOpen] = useState(false)
  const [iconError, setIconError] = useState(null)
  // Modal de confirmação destrutiva quando troca de Virtual Network
  // (vnet) — exige type-to-confirm + alerta sobre re-bootstrap.
  const [vnetConfirmOpen, setVnetConfirmOpen] = useState(false)

  // Lista de Virtual Networks do tenant pra povoar o select. Mesma origem
  // que CreateDeviceModal usa.
  const tenantId = device?.tenantId
  const { data: vpnNetworks = [], isLoading: vpnNetworksLoading } = useQuery({
    queryKey: ['vpnNetworks', tenantId],
    queryFn: () => vpnNetworksApi.getByTenant(tenantId),
    enabled: !!tenantId,
  })

  useEffect(() => { setForm(snapshot(device)) }, [device])

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot(device))
  const vnetChanged = (form.vpnNetworkId ?? null) !== (device?.vpnNetworkId ?? null)
  const currentVnet = vpnNetworks.find((n) => n.id === device?.vpnNetworkId)
  const selectedVnet = vpnNetworks.find((n) => n.id === form.vpnNetworkId)

  const handleSaveIcon = async (jpegDataUrl) => {
    setIconError(null)
    try {
      await updateIcon.mutateAsync({ id: device.id, customIconBase64: jpegDataUrl })
      setIconPickerOpen(false)
    } catch (e) {
      setIconError(e.response?.data?.message || e.message || 'Erro ao guardar ícone')
    }
  }

  const handleClearCustomIcon = async () => {
    setIconError(null)
    try {
      await updateIcon.mutateAsync({ id: device.id, customIconBase64: null })
    } catch (e) {
      setIconError(e.response?.data?.message || e.message || 'Erro ao remover ícone')
    }
  }

  // Save efetivo (chamado direto se nada destrutivo, ou após confirmação do
  // modal danger se vnet mudou).
  const performSave = async () => {
    setError(null)
    try {
      const payload = { ...form }
      // Não enviar campos vazios string para evitar sobrescrever com null indesejadamente
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') payload[k] = null
      })
      // Backend exige confirmVnetChange=true quando troca peer existente.
      // Aqui só chegamos via modal de confirmação destrutivo, então enviar
      // sempre que a vnet tiver mudado.
      if (vnetChanged) payload.confirmVnetChange = true
      await update.mutateAsync({ id: device.id, data: payload })
      setSavedAt(Date.now())
      setVnetConfirmOpen(false)
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Erro ao salvar')
    }
  }

  const handleSave = () => {
    // Troca de Virtual Network força confirmação destrutiva: backend vai
    // deletar peer (libera IP) + criar novo (IP novo) + device fica offline
    // até rodar `sudo automais-device install` de novo no device.
    if (vnetChanged) {
      setVnetConfirmOpen(true)
      return
    }
    performSave()
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Pencil className="w-4 h-4 text-theme-primary" />
        <h3 className="text-sm font-semibold text-gray-900">{t('managedDeviceDetail.editTitle', 'Edição')}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS
          .filter((f) => !f.section || (f.section === 'router' && isRouter) || (f.section === 'host' && !isRouter && !isMobile))
          .map((f) => (
            <label key={f.key} className="block">
              <span className="text-xs font-medium text-gray-600">{f.label}</span>
              <input
                type={f.type || 'text'}
                value={form[f.key] ?? ''}
                readOnly={!!f.readOnly}
                onChange={(e) => {
                  const v = f.type === 'number'
                    ? (e.target.value === '' ? null : Number(e.target.value))
                    : e.target.value
                  setForm((s) => ({ ...s, [f.key]: v }))
                }}
                className={`mt-1 input w-full text-sm ${f.readOnly ? 'bg-gray-50 text-gray-500' : ''}`}
              />
            </label>
          ))}

        {/* Virtual Network (vnet/VPN) — campo destrutivo: troca exige
            re-bootstrap do device pra restabelecer VPN. */}
        <label className="block md:col-span-2">
          <span className="text-xs font-medium text-gray-600">
            {t('managedDeviceDetail.virtualNetwork', 'Virtual Network')}
          </span>
          <select
            value={form.vpnNetworkId ?? ''}
            onChange={(e) =>
              setForm((s) => ({ ...s, vpnNetworkId: e.target.value || null }))
            }
            disabled={vpnNetworksLoading}
            className={`mt-1 input w-full text-sm ${
              vnetChanged ? 'border-red-400 ring-1 ring-red-300' : ''
            }`}
          >
            <option value="">
              {t('managedDeviceDetail.virtualNetworkNone', '— Sem Virtual Network —')}
            </option>
            {vpnNetworks.map((net) => (
              <option key={net.id} value={net.id}>
                {net.name}
                {net.cidr ? ` (${net.cidr})` : ''}
              </option>
            ))}
          </select>
          {vnetChanged && (
            <p className="mt-1 text-xs text-red-600">
              {t(
                'managedDeviceDetail.virtualNetworkChangedHint',
                'Trocar a Virtual Network interrompe a VPN e exige re-bootstrap do device. Confirmação será pedida ao salvar.',
              )}
            </p>
          )}
        </label>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <span className="text-xs font-medium text-gray-600">
          {t('managedDeviceDetail.deviceIcon', 'Ícone do dispositivo')}
        </span>
        <p className="text-[11px] text-gray-500 mt-0.5 mb-2">
          {t(
            'managedDeviceDetail.deviceIconHint',
            'Por padrão usa-se o ícone do tipo (Linux, Proxmox, etc.). Você pode substituir por uma imagem (recorte quadrado automático, 128×128, JPEG), como na lista de hosts/routers.'
          )}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 border border-gray-200 overflow-hidden">
            <DeviceKindIcon
              kind={device.kind}
              kindIconFileName={device.kindIconFileName}
              customIconBase64={device.customIconBase64}
              size={40}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => { setIconError(null); setIconPickerOpen(true) }}
              disabled={updateIcon.isPending}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <ImageIcon className="w-4 h-4" />
              {t('managedDeviceDetail.changeDeviceIcon', 'Alterar ícone')}
            </button>
            {device.customIconBase64 ? (
              <button
                type="button"
                onClick={handleClearCustomIcon}
                disabled={updateIcon.isPending}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                {t('managedDeviceDetail.restoreKindIcon', 'Restaurar ícone do tipo')}
              </button>
            ) : null}
          </div>
        </div>
        {iconError && <p className="text-xs text-red-600 mt-2">{iconError}</p>}
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-xs text-gray-500">
          {error && <span className="text-red-600">{error}</span>}
          {!error && savedAt && <span className="text-green-600">{t('common.saved', 'Salvo.')}</span>}
        </div>
        <button
          type="button"
          disabled={!dirty || update.isPending}
          onClick={handleSave}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-theme-primary hover:bg-theme-primary/90 disabled:opacity-50 rounded"
        >
          {update.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('common.buttons.save', 'Salvar')}
        </button>
      </div>

      <IconPickerModal
        isOpen={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        onSave={handleSaveIcon}
        title={`Ícone de "${device.name}"`}
        loading={updateIcon.isPending}
      />

      <ChangeVirtualNetworkConfirmModal
        isOpen={vnetConfirmOpen}
        onClose={() => setVnetConfirmOpen(false)}
        onConfirm={performSave}
        deviceName={device?.name}
        fromNetworkName={currentVnet?.name || '— sem rede —'}
        toNetworkName={selectedVnet?.name || '— sem rede —'}
        isPending={update.isPending}
      />
    </section>
  )
}

function snapshot(d) {
  if (!d) return {}
  return {
    name: d.name ?? '',
    description: d.description ?? '',
    vpnNetworkId: d.vpnNetworkId ?? null,
    sshPort: d.sshPort ?? null,
    remoteDisplayPort: d.remoteDisplayPort ?? null,
    apiUrl: d.apiUrl ?? '',
    apiUsername: d.apiUsername ?? '',
    firmwareVersion: d.firmwareVersion ?? '',
    model: d.model ?? '',
    serialNumber: d.serialNumber ?? '',
  }
}
